import { eventMap as headEventMap } from "../../../core/components/table/head/row";
import {
    componentClasses,
    componentName,
    defaultProps,
    eventMap,
    parseCellsMap,
    schema,
} from "../../../core/components/table/table";
import DataGrid from "../../../core/js/data-grid/data-grid";
import { types as sortType } from "../../../core/js/data-grid/sort-by-column";
import sortByColumn, { types } from "../../../core/js/data-grid/sort-by-column";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import { dispatchEvent, renderState } from "../../../core/js/state-machine";
import { matchers } from "../../../core/js/validate-props";
import TableHead from "./head/row";
import TableRow from "./body/row";
import { debounce } from "../../../core/js/utilities";
import { subscribe } from "../../../core/js/icc/broker";
import { eventMap as paginationEventMap } from "../../../core/components/pagination/pagination";

/**
 * Creates a Table and return the API
 * @param {defaultProps} props
 * @returns {{element:HTMLElement}}
 */
const Table = (element, props) => {
    const [success, mergedProps] = validateInitialization({
        element,
        componentName,
        defaultProps,
        props,
        schema,
    });
    if (!success) return;

    const dispose = () => {
        if (tableHead) {
            window.removeEventListener("resize", handleTablePinWindowResize);
            tableHead.element.removeEventListener(headEventMap.sort, handleTableHeadSortChange);
            tableHead.element.removeEventListener(headEventMap.unpin, handleTableUnpinChange);
            tableHead.element.removeEventListener(headEventMap.pin, handleTablePinChange);
            tableHead.element.removeEventListener(headEventMap.scroll, handleTableHeadTabStop);
            tableHead.element.removeEventListener(
                headEventMap.styleUpdated,
                handleTableHeadStyleUpdated
            );
            tableHead.dispose();
        }
        element[componentName] = null;
    };

    /**
     * Add new rows in the main wrapper.
     * @param {Array} data the data that will be sent to the TableRow
     * @param {Number} offset the qtd of rows you already have to be used as index.
     * Consider that you have 10 rows and a new page arrive with 12. This function will be
     * called with [x,y] and 10, so new rows will be 11 and 12.
     */
    const addRows = (data, offset = 0) => {
        //@perf using document fragment to make a single append in the main element
        const frag = document.createDocumentFragment();
        for (let index = 0; index < data.length; index++) {
            const row = TableRow({
                id: offset + index,
                data: data[index],
                columns: tableHead.columns,
            });
            rowsList.push(row);
            frag.appendChild(row.element);
        }
        element.appendChild(frag);
    };

    /**
     * This is function is called whenever the data changes.
     * It will track for more/less rows (when changing page/filter/search etc) and update existent ones
     * @param {Object} summary
     */
    const updateTableData = (summary) => {
        const newData = dataGrid.getPageData(summary.currentPage);
        const rowsOffset = newData.length - rowsList.length;
        const firstRowIndex = summary.firstRowIndexOnPage;
        // Update existent rows with new data
        rowsList.forEach((row, index) => {
            if (index < newData.length) {
                row.update({
                    id: firstRowIndex + index,
                    data: newData[index],
                    columns: tableHead.columns,
                });
            }
        });
        // Checking if more rows are needed
        if (rowsOffset > 0) {
            const startSlice = newData.length - rowsOffset;
            addRows(newData.slice(startSlice), startSlice);
        }
        // Checking extra rows to be removed
        else if (rowsOffset < 0) {
            let rowsToRemove = Math.abs(rowsOffset);
            while (rowsToRemove--) {
                const row = rowsList.pop();
                if (row.dispose) row.dispose();
                element.removeChild(row.element);
            }
        }
        dispatchEvent(eventMap.componentRender, element);
    };

    const parseDataGrid = (cols) => {
        return cols.map(parseCellsMap);
    };
    const sortData = (columnId, sortBy) => {
        let data;
        unsortedDataGrid = unsortedDataGrid ? unsortedDataGrid : dataGrid.getDataGrid().concat([]);
        if (sortBy === types.unsorted) {
            // Reset sort and restore original data;
            data = unsortedDataGrid;
            unsortedDataGrid = null;
        } else {
            // Apply the sort on the data grid
            data = dataGrid.getDataGrid();
            data.sort(sortByColumn(columnId, sortBy));
        }
        dataGrid = DataGrid({
            rowsPerPage: dataGrid.getRowsPerPage(),
            currentPage: dataGrid.getCurrentPage(),
            data,
        });
    };

    /**
     * Sort dataGrid data and update table
     * @param {{columnId:Number,sortBy:string}} config
     */
    const sort = (columnId = 0, sortBy) => {
        if (!matchers.string(sortBy)) {
            sortBy = "unsorted";
        }
        if (!(sortBy in types) || !dataGrid.hasCol(columnId)) {
            console.warn(`${componentName} invalid columnId or sortBy`);
            return;
        }
        sortData(columnId, sortBy);
        updateTableData(dataGrid.getSummary());
        dispatchEvent(eventMap.sort, element, { sortBy, columnId });
    };

    const handleTableHeadStyleUpdated = (event) => {
        const classList = event.detail.classList;
        const columnClassName = event.detail.columnClassName;

        element.querySelectorAll(`.${columnClassName}`).forEach((column) => {
            renderState({ classList }, column);
        });
    };

    const handleTableHeadSortChange = (event) => {
        if (event.detail.sortBy) {
            api.sort(event.detail.columnId, event.detail.sortBy);
        }
    };

    /**
     * Pin the specified column
     * @param {Number} columnId the columnId to be pinned
     */
    const pin = (columnId = 0) => {
        const colsLength = dataGrid.getTotalCols();
        const lastPinnableColumn = colsLength - 3;

        // Check if is greater than zero the columnId, or greater than the available columsn
        if (!matchers.number(columnId) || columnId < 0) {
            console.warn(`${componentName} invalid columnId`);
            return;
        }

        // Check if the pin is possible with the current column length
        if (colsLength < 3) {
            console.warn(`${componentName} pin can only be invoked with at least 3 columns`);
            return;
        }

        /*
         * Check if the columnId can be used to pin, using this rule:
         *  - If the table have 4 columns, only the first two can be pinnable
         *  - If the table have 5 columns, only the first three can pinnable
         *  - In other words, it must have always two columns that cannot be pinned,
         *  watching the pattern above.
         */
        if (columnId > lastPinnableColumn) {
            console.warn(
                `${componentName} pin can only be used on the first ${
                    lastPinnableColumn + 1
                } columns`
            );
            return;
        }

        pinColumn(columnId);
    };

    /**
     * Pin the specified column
     * @param {Number} columnId Column to be pinned
     */
    const pinColumn = (columnId) => {
        if (mergedProps.pinnable) {
            tableHead.pin(columnId);
            window.addEventListener("resize", handleTablePinWindowResize);
        }
    };

    /**
     * Unpin the columns
     */
    const unpin = () => {
        if (mergedProps.pinnable) {
            tableHead.unpin();
            window.removeEventListener("resize", handleTablePinWindowResize);
        }
    };

    const handleTablePinChange = (event) => {
        api.pin(event.detail.columnId);
    };

    const handleTableUnpinChange = () => {
        api.unpin();
    };

    const handleTablePinWindowResize = debounce(() => {
        tableHead.recalculatePinnedColumn();
    }, 300);

    const handleTableHeadTabStop = (event) => {
        const width = event.detail.width;
        const cell = event.detail.cell;

        // If the sum of all previous cells is larger than the
        // table element, the table must scroll to be
        // able to see the element.
        if (width + cell.offsetWidth > element.offsetWidth) {
            element.scroll({ left: width - element.offsetWidth + cell.offsetWidth });

            return;
        }

        // If the table is scrolled,
        if (element.scrollLeft > 0) {
            element.scroll({ left: element.scrollLeft - width });

            return;
        }
    };

    //HOLD IT FOR PHASE2
    //const deleteRow = (row) => {
    //    if (dataGrid && dataGrid.hasRow(row)) {
    //        dataGrid.deleteRow(row);
    //        updateTableData(dataGrid.getSummary());
    //    }
    //};
    const setCurrentPage = (page) => {
        if (dataGrid.hasPage(page)) {
            dataGrid.setCurrentPage(page);
            updateTableData(dataGrid.getSummary());
            dispatchEvent(eventMap.pageChange, element, { page });
        }
    };

    /**
     * Render table rows based of pageSize and currentPage
     * @param {Number} pageSize items visible per page.
     * @param {Number} currentPage default is 1
     */
    const setRowsPerPage = (pageSize, currentPage = 1) => {
        if (matchers.gte(1) && pageSize !== dataGrid.getRowsPerPage()) {
            if (currentPage && dataGrid.getCurrentPage() !== currentPage) {
                dataGrid.setCurrentPage(currentPage);
            }
            dataGrid.setRowsPerPage(pageSize);
            updateTableData(dataGrid.getSummary());
            dispatchEvent(eventMap.pageSizeChanged, element, { pageSize });
        }
    };

    const rowsList = [];
    let dataGrid, unsortedDataGrid;

    // Parse the data from data
    const data = mergedProps.data.map(parseDataGrid);
    const gridConfig = {
        data,
        rowsPerPage: mergedProps.pagination ? mergedProps.pagination.rowsPerPage : data.length,
        currentPage: 1,
    };

    dataGrid = DataGrid(gridConfig);
    if (!dataGrid) return;

    const tableHead = TableHead({
        data: mergedProps.columns.map(parseCellsMap),
        pinnable: mergedProps.pinnable,
        pin: mergedProps.pin,
        resizableColumns: mergedProps.resizableColumns,
    });
    if (!tableHead) {
        console.warn(`${componentName} failed to create table head`);
        return;
    }
    tableHead.element.addEventListener(headEventMap.sort, handleTableHeadSortChange);
    tableHead.element.addEventListener(headEventMap.pin, handleTablePinChange);
    tableHead.element.addEventListener(headEventMap.unpin, handleTableUnpinChange);
    tableHead.element.addEventListener(headEventMap.scroll, handleTableHeadTabStop);
    tableHead.element.addEventListener(headEventMap.styleUpdated, handleTableHeadStyleUpdated);

    // Column head is defining an initial sort to the data.
    if (tableHead.sort && tableHead.sort.sortBy !== sortType.unsorted) {
        sortData(tableHead.sort.columnId, tableHead.sort.sortBy);
    }

    // Crate a fragment to add everything at once
    element.appendChild(tableHead.element);

    // Create initial rules
    addRows(dataGrid.getPageData());

    if (mergedProps.pagination && mergedProps.pagination.topics) {
        mergedProps.pagination.topics.forEach((topic) => {
            // For each target topic capture all pagination messages that should change the table
            subscribe(
                topic,
                ({ details }) => setCurrentPage(details.currentPage),
                paginationEventMap.pageChanged
            );
            subscribe(
                topic,
                ({ details }) => setRowsPerPage(details.pageSize, details.currentPage),
                paginationEventMap.pageSizeChanged
            );
        });
    }

    dispatchEvent(eventMap.componentRender, element);

    if (mergedProps.pinnable && mergedProps.pin && mergedProps.pin.startColumn >= 0) {
        pin(mergedProps.pin.startColumn);
    }

    // Check if the columns has any sizing property.
    // Append "mergedProps.resizableColumns ||" to reenable the resize handle.
    const hasColumnWithCustomSizing = mergedProps.columns.some((col) => {
        return col.minWidth || col.maxWidth || col.width || col.fit;
    });

    // If the table columns can be resized, or have some sizing property,
    // should add a custom sizing class to have specific styling for this
    // scenario.
    if (hasColumnWithCustomSizing) {
        renderState({ classList: [componentClasses.base.tableColumnCustomSizing] }, element);

        // Find the largest width of each cell, and set the largest
        // width as the current width of the cell.
        tableHead.cells.forEach((headCell) => {
            const columnUniqueClass = headCell.data.colClass;
            const largestWidth = Array.from(
                element.querySelectorAll(`.${columnUniqueClass}`)
            ).reduce((acc, item) => {
                const width = item.getBoundingClientRect().width;
                return width > acc ? width : acc;
            }, 0);

            headCell.setWidth(largestWidth);
            headCell.updateColumnStyling();
        });
    }

    const api = {
        element,
        pin,
        unpin,
        sort,
        dispose,
    };

    element[componentName] = api;
    return api;
};

export default Table;
